import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-bissa-blue hover:text-bissa-gold transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-4xl font-bold text-bissa-blue mb-2">Refund Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          <h2>1. Overview</h2>
          <p>
            At Bissa.ai, we want you to be completely satisfied with our service. This Refund Policy 
            explains our approach to refunds and cancellations for our CRM platform services.
          </p>

          <h2>2. Subscription Plans</h2>
          <p>We offer the following subscription plans:</p>
          <ul>
            <li><strong>Free Plan:</strong> $0/month - No billing, no refunds applicable</li>
            <li><strong>Growth Plan:</strong> $15/month - Monthly billing cycle</li>
            <li><strong>Pro Plan:</strong> $35/month - Monthly billing cycle</li>
            <li><strong>Business Plan:</strong> $79/month - Monthly billing cycle</li>
          </ul>

          <h2>3. Refund Eligibility</h2>
          
          <h3>30-Day Money-Back Guarantee</h3>
          <p>
            We offer a 30-day money-back guarantee for first-time subscribers to any paid plan. 
            If you're not satisfied within the first 30 days of your initial subscription, 
            you can request a full refund.
          </p>

          <h3>Eligible Circumstances</h3>
          <p>Refunds may be granted in the following situations:</p>
          <ul>
            <li>Service unavailability for more than 48 consecutive hours</li>
            <li>Billing errors or duplicate charges</li>
            <li>Failure to deliver promised features (with 30-day notice period)</li>
            <li>Technical issues preventing normal use of the service</li>
            <li>Accidental subscription or plan changes</li>
          </ul>

          <h3>Non-Eligible Circumstances</h3>
          <p>Refunds will NOT be granted for:</p>
          <ul>
            <li>Change of mind after the 30-day guarantee period</li>
            <li>Failure to use the service due to user error or lack of training</li>
            <li>Violation of Terms of Service resulting in account suspension</li>
            <li>Third-party integration issues beyond our control</li>
            <li>Data loss due to user error or failure to backup data</li>
            <li>Dissatisfaction with features that were clearly described</li>
          </ul>

          <h2>4. Refund Process</h2>
          
          <h3>How to Request a Refund</h3>
          <ol>
            <li>Contact our support team through your Bissa.ai dashboard</li>
            <li>Provide your account details and reason for the refund request</li>
            <li>Allow up to 5 business days for review and processing</li>
            <li>Receive confirmation and timeline for refund completion</li>
          </ol>

          <h3>Processing Timeline</h3>
          <ul>
            <li><strong>Review Period:</strong> 1-5 business days</li>
            <li><strong>Refund Processing:</strong> 5-10 business days</li>
            <li><strong>Bank Processing:</strong> 3-5 business days (varies by financial institution)</li>
          </ul>

          <h2>5. Cancellation Policy</h2>
          
          <h3>How to Cancel</h3>
          <p>You can cancel your subscription at any time:</p>
          <ul>
            <li>Through your billing settings in the Bissa.ai dashboard</li>
            <li>By contacting customer support</li>
            <li>Via email to billing@bissa.ai</li>
          </ul>

          <h3>Cancellation Effects</h3>
          <ul>
            <li>Immediate cancellation: Access continues until end of billing period</li>
            <li>No automatic refund for remaining subscription time</li>
            <li>Data retention for 30 days after cancellation</li>
            <li>Account downgrade to Free plan limits</li>
          </ul>

          <h2>6. Downgrades and Plan Changes</h2>
          
          <h3>Downgrading Plans</h3>
          <p>
            When downgrading to a lower-tier plan, changes take effect at the next billing cycle. 
            No refund is provided for the difference in plan pricing.
          </p>

          <h3>Upgrading Plans</h3>
          <p>
            When upgrading, you'll be charged a prorated amount for the remaining billing period 
            and then billed at the new rate for subsequent periods.
          </p>

          <h2>7. Payment Failures and Account Suspension</h2>
          
          <h3>Failed Payments</h3>
          <p>If a payment fails:</p>
          <ul>
            <li>We'll retry the payment up to 3 times over 10 days</li>
            <li>You'll receive email notifications about failed payments</li>
            <li>Account may be suspended if payment issues aren't resolved</li>
            <li>No refund for service interruption due to payment failure</li>
          </ul>

          <h3>Account Reactivation</h3>
          <p>
            Suspended accounts can be reactivated by updating payment information 
            within 30 days of suspension.
          </p>

          <h2>8. Dispute Resolution</h2>
          
          <h3>Chargeback Policy</h3>
          <p>
            Before initiating a chargeback with your bank, please contact our support team. 
            Chargebacks may result in account suspension and additional fees.
          </p>

          <h3>Escalation Process</h3>
          <ol>
            <li>Contact customer support for initial resolution</li>
            <li>Request escalation to billing management if unsatisfied</li>
            <li>Final review by senior management team</li>
          </ol>

          <h2>9. Special Circumstances</h2>
          
          <h3>Annual Subscriptions</h3>
          <p>
            If we introduce annual subscription options in the future, separate refund terms 
            will apply and will be clearly communicated at the time of purchase.
          </p>

          <h3>Enterprise Customers</h3>
          <p>
            Enterprise customers with custom contracts may have different refund terms 
            as specified in their individual agreements.
          </p>

          <h2>10. Contact Information</h2>
          <p>For refund requests or questions about this policy:</p>
          <ul>
            <li><strong>Email:</strong> billing@bissa.ai</li>
            <li><strong>Support Dashboard:</strong> Submit a ticket through your account</li>
            <li><strong>Response Time:</strong> Within 24 hours during business days</li>
          </ul>

          <h2>11. Policy Updates</h2>
          <p>
            This Refund Policy may be updated periodically. We will notify existing customers 
            of any material changes that affect their refund rights at least 30 days in advance.
          </p>

          <div className="mt-12 p-6 bg-bissa-gold-50 rounded-lg border border-bissa-gold-200">
            <h3 className="text-bissa-blue font-semibold mb-2">Fair and Transparent</h3>
            <p className="text-bissa-blue-700">
              Our refund policy is designed to be fair and transparent. We're committed to 
              providing excellent service and will work with you to resolve any issues. 
              Don't hesitate to reach out if you have concerns about your subscription.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;